import React, { useState } from 'react';
import type { ZendeskTicketResponse, ProcessNoticeResponse } from '../types/notice';
import {
  formatDate,
  formatCurrency,
  getDateUrgency,
  getConfidenceLevel,
  getConfidenceColorClass,
  getUrgencyColorClass,
} from '../utils/validation';
import { RichText } from '../utils/linkify';
import { Button } from '@/components/ui/button';
import { HUBSPOT_MEETING_URL } from '@/config/constants';

interface HRSubmissionSuccessProps {
  ticketResult: ZendeskTicketResponse;
  noticeResult: ProcessNoticeResponse;
  onStartOver: () => void;
  onStartFresh?: () => void;
  className?: string;
}

export const HRSubmissionSuccess: React.FC<HRSubmissionSuccessProps> = ({
  ticketResult,
  noticeResult,
  onStartOver,
  className = '',
}) => {
  const [expandedSections, setExpandedSections] = useState({
    summary: false,
    dates: false,
    monetary: false,
    actions: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const displayClassification = ticketResult.notice_classification === "Unknown Notice Type"
    ? "Needs Verification"
    : ticketResult.notice_classification;

  const confidenceLevel = getConfidenceLevel(ticketResult.confidence_score);
  const confidencePercentage = Math.round(ticketResult.confidence_score * 100);

  const hasKeyDates = Object.values(noticeResult.key_dates).some(d => d !== null);
  const hasMonetaryInfo = Object.values(noticeResult.monetary_info).some(a => a !== null);
  const hasActionItems = noticeResult.action_items.length > 0;

  const SectionHeader: React.FC<{
    title: string;
    section: keyof typeof expandedSections;
    badge?: string;
  }> = ({ title, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors rounded-lg"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {badge && (
          <span className="px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
            {badge}
          </span>
        )}
      </div>
      <svg
        className={`w-4 h-4 text-muted-foreground transition-transform ${
          expandedSections[section] ? 'rotate-180' : ''
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Success Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
          <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Sent to DianaHR
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your notice has been forwarded to our HR team for handling
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
            {displayClassification}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColorClass(confidenceLevel)}`}>
            {confidencePercentage}% Confidence
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 capitalize">
            {ticketResult.priority} Priority
          </span>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground">What Happens Next</h3>
        <div className="space-y-3">
          {[
            { label: 'HR team reviews your case', detail: 'Our operators will analyze the notice and prepare recommendations', done: false },
            { label: 'You receive a detailed action plan', detail: 'Expect a response via email within 3-5 business days', done: false },
            { label: 'We help resolve the notice', detail: 'Step-by-step guidance or we handle it directly on your behalf', done: false },
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-xs font-medium text-muted-foreground">{i + 1}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{step.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analysis Summary */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground text-center uppercase tracking-wide font-medium">
          Analysis sent to HR team
        </p>

        {/* Executive Summary */}
        <div className="rounded-xl border bg-card">
          <SectionHeader title="Executive Summary" section="summary" />
          <div
            className="grid"
            style={{
              gridTemplateRows: expandedSections.summary ? '1fr' : '0fr',
              transition: 'grid-template-rows 300ms ease-in-out',
            }}
          >
            <div style={{ overflow: 'hidden', minHeight: 0 }}>
              <div className="px-4 pb-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {noticeResult.executive_summary}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Dates */}
        {hasKeyDates && (
          <div className="rounded-xl border bg-card">
            <SectionHeader
              title="Important Dates"
              section="dates"
              badge={Object.values(noticeResult.key_dates).filter(d => d).length.toString()}
            />
            <div
              className="grid"
              style={{
                gridTemplateRows: expandedSections.dates ? '1fr' : '0fr',
                transition: 'grid-template-rows 300ms ease-in-out',
              }}
            >
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div className="px-4 pb-4 space-y-2">
                  {Object.entries(noticeResult.key_dates).map(([key, date]) => {
                    if (!date) return null;
                    const urgency = getDateUrgency(date);
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div
                        key={key}
                        className={`flex items-center justify-between p-3 rounded-lg border ${getUrgencyColorClass(urgency)}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{label}</p>
                          <p className="text-xs mt-0.5">{formatDate(date)}</p>
                        </div>
                        {urgency === 'urgent' && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">Urgent</span>
                        )}
                        {urgency === 'warning' && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">Soon</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monetary Information */}
        {hasMonetaryInfo && (
          <div className="rounded-xl border bg-card">
            <SectionHeader title="Financial Information" section="monetary" />
            <div
              className="grid"
              style={{
                gridTemplateRows: expandedSections.monetary ? '1fr' : '0fr',
                transition: 'grid-template-rows 300ms ease-in-out',
              }}
            >
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div className="px-4 pb-4 space-y-2">
                  {Object.entries(noticeResult.monetary_info).map(([key, amount]) => {
                    if (amount === null) return null;
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    const isTotal = key === 'total_amount';
                    return (
                      <div
                        key={key}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isTotal ? 'bg-muted border border-border' : 'bg-muted/50'
                        }`}
                      >
                        <span className={`text-sm ${isTotal ? 'font-semibold' : 'font-medium'} text-foreground`}>
                          {label}
                        </span>
                        <span className={`text-sm ${isTotal ? 'font-bold' : 'font-medium'} text-foreground`}>
                          {formatCurrency(amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Items */}
        {hasActionItems && (
          <div className="rounded-xl border bg-card">
            <SectionHeader
              title="Action Items"
              section="actions"
              badge={noticeResult.action_items.length.toString()}
            />
            <div
              className="grid"
              style={{
                gridTemplateRows: expandedSections.actions ? '1fr' : '0fr',
                transition: 'grid-template-rows 300ms ease-in-out',
              }}
            >
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div className="px-4 pb-4 space-y-2">
                  {noticeResult.action_items
                    .sort((a, b) => {
                      const order = { High: 1, Medium: 2, Low: 3 };
                      return order[a.priority] - order[b.priority];
                    })
                    .map((item, index) => {
                      const priorityBadgeColors = {
                        High: 'bg-red-100 text-red-800',
                        Medium: 'bg-yellow-100 text-yellow-800',
                        Low: 'bg-emerald-100 text-emerald-800',
                      };
                      return (
                        <div key={index} className="flex items-start justify-between gap-2 p-3 rounded-lg bg-muted/50">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground">{item.action}</p>
                            {item.deadline && (
                              <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getUrgencyColorClass(getDateUrgency(item.deadline))}`}>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Due: {formatDate(item.deadline)}
                              </div>
                            )}
                            {item.details && (
                              <div className="mt-2">
                                <RichText text={item.details} />
                              </div>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${priorityBadgeColors[item.priority]}`}>
                            {item.priority}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reference */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Classification</p>
            <p className="text-sm font-medium text-foreground mt-0.5">{displayClassification}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Confidence</p>
            <p className="text-sm font-medium text-foreground mt-0.5">{confidencePercentage}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Priority</p>
            <p className="text-sm font-medium text-foreground mt-0.5 capitalize">{ticketResult.priority}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Response</p>
            <p className="text-sm font-medium text-foreground mt-0.5">3-5 days</p>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-foreground text-center">What would you like to do next?</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button className="h-auto py-4 flex-col gap-1" asChild>
            <a
              href={HUBSPOT_MEETING_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="text-sm font-semibold">Book a Call</span>
              <span className="text-xs opacity-80 font-normal">Talk to an HR expert about this notice</span>
            </a>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-1"
            onClick={onStartOver}
          >
            <span className="text-sm font-semibold">Process Another Notice</span>
            <span className="text-xs text-muted-foreground font-normal">Your info is saved for next time</span>
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center pt-1">
          Need help now?{' '}
          <a href={HUBSPOT_MEETING_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">
            Book a call
          </a>{' '}
          with an HR expert.
        </p>
      </div>
    </div>
  );
};
